import { create } from 'zustand';
import { persist, createJSONStorage } from 'zustand/middleware';

export interface LapairCartItem {
  id: string; // frameId + lensTypeId composite key
  frameId: string;
  frameName: string;
  frameSlug: string;
  frameImageUrl: string | null;
  lensTypeId: string;
  lensDisplayName: string;
  countryId: string;
  currencyCode: string;
  framePriceTtc: number;
  lensPriceTtc: number;
  totalLineTtc: number;
  quantity: number;
}

interface CartStore {
  items: LapairCartItem[];
  pickupBranchId: string | null;
  pickupBranchName: string | null;
  addItem: (item: Omit<LapairCartItem, 'id' | 'quantity'> & { quantity?: number }) => void;
  updateQuantity: (id: string, quantity: number) => void;
  removeItem: (id: string) => void;
  removeItems: (ids: string[]) => void;
  updateItemLens: (id: string, lens: { lensTypeId: string; lensDisplayName: string; lensPriceTtc: number }) => void;
  clearCart: () => void;
  setPickupBranch: (id: string | null, name: string | null) => void;
  couponCode: string | null;
  couponPercent: number | null;
  setCoupon: (code: string | null, percent: number | null) => void;
  /** Total après remise (arrondi par ligne, comme le serveur). */
  discountedTotal: () => number;
  totalItems: () => number;
  totalPrice: () => number;
}

function makeId(frameId: string, lensTypeId: string) {
  return `${frameId}__${lensTypeId}`;
}

export const useCartStore = create<CartStore>()(
  persist(
    (set, get) => ({
      items: [],
      pickupBranchId: null,
      pickupBranchName: null,

      setPickupBranch: (id, name) => set({ pickupBranchId: id, pickupBranchName: name }),
      couponCode: null,
      couponPercent: null,
      setCoupon: (code, percent) => set({ couponCode: code, couponPercent: percent }),
      discountedTotal: () => {
        const p = get().couponPercent ?? 0;
        return get().items.reduce((sum, i) => {
          const v = i.totalLineTtc * (1 - p / 100);
          const unit = ["XOF", "XAF", "UGX", "TZS", "CDF", "RWF"].includes(i.currencyCode.toUpperCase())
            ? Math.round(v) : Math.round(v * 100) / 100;
          return sum + unit * i.quantity;
        }, 0);
      },

      addItem: (itemData) => {
        const id = makeId(itemData.frameId, itemData.lensTypeId);
        const qty = itemData.quantity ?? 1;
        const { items } = get();
        const existing = items.find((i) => i.id === id);

        if (existing) {
          set({
            items: items.map((i) =>
              i.id === id ? { ...i, quantity: i.quantity + qty } : i
            ),
          });
        } else {
          set({
            items: [
              ...items,
              { ...itemData, id, quantity: qty, totalLineTtc: itemData.framePriceTtc + itemData.lensPriceTtc },
            ],
          });
        }

        // Analytics — add_to_cart (GA4 conversion)
        import("@/lib/analytics/track").then(({ track }) => {
          track("add_to_cart", {
            product_id: itemData.frameId,
            product_name: itemData.frameName,
            product_slug: itemData.frameSlug,
            lens_type_id: itemData.lensTypeId,
            lens_name: itemData.lensDisplayName,
            quantity: qty,
            price: itemData.framePriceTtc + itemData.lensPriceTtc,
            currency_code: itemData.currencyCode,
          });
        }).catch(() => {});
      },

      updateQuantity: (id, quantity) => {
        if (quantity <= 0) {
          get().removeItem(id);
          return;
        }
        set({ items: get().items.map((i) => (i.id === id ? { ...i, quantity } : i)) });
      },

      removeItem: (id) => {
        set({ items: get().items.filter((i) => i.id !== id) });
      },

      updateItemLens: (id, lens) => {
        const { items } = get();
        const item = items.find((i) => i.id === id);
        if (!item) return;
        const newId = makeId(item.frameId, lens.lensTypeId);
        const updated = { ...item, id: newId, lensTypeId: lens.lensTypeId, lensDisplayName: lens.lensDisplayName,
          lensPriceTtc: lens.lensPriceTtc, totalLineTtc: item.framePriceTtc + lens.lensPriceTtc };
        const other = items.find((i) => i.id === newId && i.id !== id);
        set({
          items: other
            ? items.filter((i) => i.id !== id).map((i) => (i.id === newId ? { ...i, quantity: i.quantity + item.quantity } : i))
            : items.map((i) => (i.id === id ? updated : i)),
        });
      },

      removeItems: (ids) => {
        const idsToRemove = new Set(ids);
        set({ items: get().items.filter((i) => !idsToRemove.has(i.id)) });
      },

      clearCart: () => set({ items: [], pickupBranchId: null, pickupBranchName: null, couponCode: null, couponPercent: null }),

      totalItems: () => get().items.reduce((sum, i) => sum + i.quantity, 0),

      totalPrice: () =>
        get().items.reduce((sum, i) => sum + i.totalLineTtc * i.quantity, 0),
    }),
    {
      name: 'lapaire-cart',
      storage: createJSONStorage(() => localStorage),
      partialize: (state) => ({
        items: state.items,
        pickupBranchId: state.pickupBranchId,
        pickupBranchName: state.pickupBranchName,
        couponCode: state.couponCode,
        couponPercent: state.couponPercent,
      }),
    }
  )
);
